import React, { createContext, useContext, useState, useEffect, Suspense, lazy } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";

// Eagerly load Home (critical path) and shared components
import Home from "./pages/Home";
import Layout from "./components/Layout";
import ChatWidget from "./components/ChatWidget";
import IndustrySelector from "./components/IndustrySelector";
import ScrollToTop from "./components/ScrollToTop";
import { CookieConsentProvider } from "./components/CookieConsentProvider";
import CookieBanner from "./components/CookieBanner";
import { Toaster } from "./components/ui/sonner";

// Lazy load all other pages (code splitting)
const PlantHire = lazy(() => import("./pages/PlantHire"));
const MobileCranes = lazy(() => import("./pages/MobileCranes"));
const CivilConstruction = lazy(() => import("./pages/CivilConstruction"));
const AllocationsScheduling = lazy(() => import("./pages/AllocationsScheduling"));
const MobileDockets = lazy(() => import("./pages/MobileDockets"));
const Compliance = lazy(() => import("./pages/Compliance"));
const Guardian = lazy(() => import("./pages/Guardian"));
const Custom = lazy(() => import("./pages/Custom"));
const Core = lazy(() => import("./pages/Core"));
const EBA = lazy(() => import("./pages/EBA"));
const YourIndustry = lazy(() => import("./pages/YourIndustry"));
const TruckCartage = lazy(() => import("./pages/TruckCartage"));
const ConcretePumping = lazy(() => import("./pages/ConcretePumping"));
const RoadSweeping = lazy(() => import("./pages/RoadSweeping"));
const PreferredPartners = lazy(() => import("./pages/PreferredPartners"));
const News = lazy(() => import("./pages/News"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Contact = lazy(() => import("./pages/Contact"));
const BookDemo = lazy(() => import("./pages/BookDemo"));
const EarlyAdopter = lazy(() => import("./pages/EarlyAdopter"));
const CaseStudies = lazy(() => import("./pages/CaseStudies"));
const About = lazy(() => import("./pages/About"));
const EquiprCivil = lazy(() => import("./pages/EquiprCivil"));
const EquiprHorizon = lazy(() => import("./pages/EquiprHorizon"));
const CoreLanding = lazy(() => import("./pages/CoreLanding"));
const PlanningBoardDemo = lazy(() => import("./pages/PlanningBoardDemo"));
const MobileCranesLP = lazy(() => import("./pages/lp/MobileCranesLP"));
const GeoCranes = lazy(() => import("./pages/geo/GeoCranes"));
const GeoPlantHire = lazy(() => import("./pages/geo/GeoPlantHire"));
const GeoCivil = lazy(() => import("./pages/geo/GeoCivil"));
const GeoTruckCartage = lazy(() => import("./pages/geo/GeoTruckCartage"));
const GeoConcrete = lazy(() => import("./pages/geo/GeoConcrete"));
const GeoSweeping = lazy(() => import("./pages/geo/GeoSweeping"));
const GeoCraneMaintenance = lazy(() => import("./pages/geo/GeoCraneMaintenance"));
const GeoCraneMaintenanceBuyer = lazy(() => import("./pages/geo/GeoCraneMaintenanceBuyer"));
const Cica2026 = lazy(() => import("./pages/Cica2026"));
const LegalHub = lazy(() => import("./pages/legal/LegalHub"));
const LegalDocument = lazy(() => import("./pages/legal/LegalDocument"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const ResetPassword = lazy(() => import("./pages/admin/ResetPassword"));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Loading fallback
const PageLoader = () => (
  <div className="min-h-screen bg-equipr-dark flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-equipr-yellow border-t-transparent rounded-full animate-spin"></div>
  </div>
);

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('equipr_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (e) {
          localStorage.removeItem('equipr_token');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('equipr_token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('equipr_token');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <PageLoader />;
  }
  
  if (!user) {
    return <Navigate to="/admin/login" replace />;
  }
  
  return children;
};

function App() {
  return (
    <CookieConsentProvider>
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<Layout><Home /></Layout>} />
              <Route path="/plant-hire-software" element={<Layout><PlantHire /></Layout>} />
              <Route path="/mobile-crane-hire-rigging" element={<Layout><MobileCranes /></Layout>} />
              <Route path="/civil-construction-operations-software" element={<Layout><CivilConstruction /></Layout>} />
              <Route path="/truck-cartage-software" element={<Layout><TruckCartage /></Layout>} />
              <Route path="/concrete-pumping-software" element={<Layout><ConcretePumping /></Layout>} />
              <Route path="/road-sweeping-software" element={<Layout><RoadSweeping /></Layout>} />
              <Route path="/allocations-scheduling-operated-plant-hire" element={<Layout><AllocationsScheduling /></Layout>} />
              <Route path="/operator-mobile-dockets-timesheets" element={<Layout><MobileDockets /></Layout>} />
              <Route path="/compliance-safety-management" element={<Layout><Compliance /></Layout>} />
              <Route path="/servicing-and-maintenance-software" element={<Layout><Guardian /></Layout>} />
              <Route path="/guardian" element={<Layout><Guardian /></Layout>} />
              <Route path="/custom-solutions" element={<Layout><Custom /></Layout>} />
              <Route path="/core" element={<Layout><Core /></Layout>} />
              <Route path="/eba" element={<Layout><EBA /></Layout>} />
              <Route path="/your-industry" element={<Layout><YourIndustry /></Layout>} />
              <Route path="/preferred-partners" element={<Layout><PreferredPartners /></Layout>} />
              <Route path="/news" element={<Layout><News /></Layout>} />
              <Route path="/news/:slug" element={<Layout><BlogPost /></Layout>} />
              <Route path="/contact-us" element={<Layout><Contact /></Layout>} />
              <Route path="/book-a-demo" element={<Layout><BookDemo /></Layout>} />
              <Route path="/early-adopter" element={<Layout><EarlyAdopter /></Layout>} />
              <Route path="/case-studies" element={<Layout><CaseStudies /></Layout>} />
              <Route path="/about" element={<Layout><About /></Layout>} />
              <Route path="/civil" element={<Layout><EquiprCivil /></Layout>} />
              <Route path="/horizon" element={<Layout><EquiprHorizon /></Layout>} />

              {/* Legal & Trust Centre */}
              <Route path="/legal" element={<Layout><LegalHub /></Layout>} />
              <Route path="/legal/terms" element={<Layout><LegalDocument slugOverride="terms" /></Layout>} />
              <Route path="/legal/privacy" element={<Layout><LegalDocument slugOverride="privacy" /></Layout>} />
              <Route path="/legal/acceptable-use" element={<Layout><LegalDocument slugOverride="acceptable-use" /></Layout>} />
              <Route path="/legal/contact" element={<Layout><LegalDocument slugOverride="contact" /></Layout>} />
              <Route path="/trust/security" element={<Layout><LegalDocument slugOverride="security" /></Layout>} />
              <Route path="/trust/data-processing" element={<Layout><LegalDocument slugOverride="data-processing" /></Layout>} />

              {/* Redirect old privacy policy to new location */}
              <Route path="/privacy-policy" element={<Navigate to="/legal/privacy" replace />} />
              
              {/* Landing Pages (no nav/footer for conversion focus) */}
              <Route path="/core/get-started" element={<CoreLanding />} />
              <Route path="/core/planning-board" element={<PlanningBoardDemo />} />
              <Route path="/lp/mobile-cranes" element={<MobileCranesLP />} />
              <Route path="/geo/best-crane-fleet-management-software" element={<GeoCranes />} />
              <Route path="/geo/crane-maintenance-compliance-tracking" element={<GeoCraneMaintenance />} />
              <Route path="/geo/best-crane-maintenance-compliance-software" element={<GeoCraneMaintenanceBuyer />} />
              <Route path="/cica2026" element={<Cica2026 />} />
              <Route path="/geo/best-plant-hire-software" element={<GeoPlantHire />} />
              <Route path="/geo/best-civil-construction-software" element={<GeoCivil />} />
              <Route path="/geo/best-truck-cartage-software" element={<GeoTruckCartage />} />
              <Route path="/geo/best-concrete-pumping-software" element={<GeoConcrete />} />
              <Route path="/geo/best-road-sweeping-software" element={<GeoSweeping />} />

              {/* Admin Routes */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/reset-password" element={<ResetPassword />} />
              <Route path="/admin/*" element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              } />
            </Routes>
          </Suspense>
          <IndustrySelector />
          <CookieBanner />
        </BrowserRouter>
        <ChatWidget />
        <Toaster position="top-right" />
      </div>
    </AuthProvider>
    </CookieConsentProvider>
  );
}

export default App;
